import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Truck,
  MapPin,
  Package,
  Check,
  Clock,
  Navigation,
  Phone,
  User,
  Printer,
  AlertCircle,
  Loader2,
  RefreshCcw,
  Bike
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { deliveryBoardService, DeliveryBoardOrder } from '@/services/deliveryBoardService';
import { orderStatusService } from '@/services/orderStatusService';

const STATUS_GROUPS: Record<string, string[]> = {
  all: [],
  pending: ['Pendente'],
  preparing: ['EmSeparacao'],
  ready: ['Pronto'],
  out: ['EmRota'],
  completed: ['Entregue', 'Retirado', 'Devolvido', 'Cancelado']
};

const DeliveryPage: React.FC = () => {
  const [orders, setOrders] = useState<DeliveryBoardOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await deliveryBoardService.list();
      if (response?.success) {
        setOrders(response.pedidos || []);
      } else {
        toast({
          title: 'Falha ao carregar entregas',
          description: response?.message || 'Não foi possível consultar o ERP.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao consultar entregas',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 60_000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const getOrderStatus = (order: DeliveryBoardOrder) => order.entregaStatus?.status || 'Pendente';
  const getOrderType = (order: DeliveryBoardOrder) => order.entregaStatus?.tipoPedido || 'delivery';

  const statusCounts = useMemo(() => {
    const counts = { all: orders.length, pending: 0, preparing: 0, ready: 0, out: 0, completed: 0 };
    orders.forEach(order => {
      const status = getOrderStatus(order);
      Object.entries(STATUS_GROUPS).forEach(([key, list]) => {
        if (key === 'all') return;
        if (list.includes(status)) counts[key as keyof typeof counts]++;
      });
    });
    return counts;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (activeTab === 'all') return orders;
    const group = STATUS_GROUPS[activeTab] || [];
    return orders.filter(order => group.includes(getOrderStatus(order)));
  }, [orders, activeTab]);

  const updateDeliveryStatus = async (order: DeliveryBoardOrder, newStatus: string) => {
    setUpdating(order.vendaID);
    try {
      const payload = { ...order.entregaStatus, status: newStatus };
      await orderStatusService.setEntregaStatus(order.vendaID, payload);
      toast({
        title: 'Status atualizado',
        description: `Pedido ${order.vendaID} agora está como ${newStatus}`
      });
      await fetchOrders();
    } catch (error) {
      toast({
        title: 'Erro ao atualizar status',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
    } finally {
      setUpdating(null);
    }
  };

  const getStatusLabel = (status?: string): string => {
    switch (status) {
      case 'Pendente':
        return 'Aguardando';
      case 'EmSeparacao':
        return 'Em Separação';
      case 'Pronto':
        return 'Pronto';
      case 'EmRota':
        return 'Em Rota';
      case 'Entregue':
        return 'Entregue';
      case 'Retirado':
        return 'Retirado';
      case 'Devolvido':
        return 'Devolvido';
      case 'Cancelado':
        return 'Cancelado';
      default:
        return 'Novo';
    }
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'EmSeparacao':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Pronto':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'EmRota':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Entregue':
      case 'Retirado':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'Devolvido':
      case 'Cancelado':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-orange-100 text-orange-800 border-orange-300';
    }
  };

  const getNextActions = (order: DeliveryBoardOrder) => {
    const status = getOrderStatus(order);
    const isDelivery = getOrderType(order) === 'delivery';

    switch (status) {
      case 'Pendente':
        return [
          { label: 'Enviar para Separação', status: 'EmSeparacao', icon: <Package className="h-4 w-4" /> }
        ];
      case 'EmSeparacao':
        return [
          { label: 'Marcar como Pronto', status: 'Pronto', icon: <Check className="h-4 w-4" /> }
        ];
      case 'Pronto':
        return isDelivery
          ? [{ label: 'Saiu para Entrega', status: 'EmRota', icon: <Navigation className="h-4 w-4" /> }]
          : [{ label: 'Cliente Retirou', status: 'Retirado', icon: <Check className="h-4 w-4" /> }];
      case 'EmRota':
        return [
          { label: 'Marcar como Entregue', status: 'Entregue', icon: <Check className="h-4 w-4" /> }
        ];
      default:
        return [];
    }
  };

  const getTimeElapsed = (date: string) => {
    return formatDistanceToNow(new Date(date), {
      addSuffix: false,
      locale: ptBR
    });
  };

  const renderAddress = (order: DeliveryBoardOrder) => {
    const endereco = order.entregaStatus?.endereco;
    if (!endereco) return null;
    return (
      <div className="text-sm bg-muted/50 p-2 rounded">
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-green-600 mt-0.5" />
          <div>
            <p>
              {endereco.street || endereco.logradouro || ''}
              {endereco.number || endereco.numero ? `, ${endereco.number || endereco.numero}` : ''}
              {endereco.complement || endereco.complemento ? ` - ${endereco.complement || endereco.complemento}` : ''}
            </p>
            <p className="text-muted-foreground">
              {(endereco.neighborhood || endereco.bairro || '')}
              {endereco.city || endereco.municipio ? `, ${endereco.city || endereco.municipio}` : ''}
            </p>
            {(endereco.reference || endereco.referencia) && (
              <p className="text-xs italic text-muted-foreground">
                Ref: {endereco.reference || endereco.referencia}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const OrderCard = ({ order }: { order: DeliveryBoardOrder }) => {
    const statusLabel = getStatusLabel(getOrderStatus(order));
    const actions = getNextActions(order);
    const isDelivery = getOrderType(order) === 'delivery';
    const isUpdating = updating === order.vendaID;

    return (
      <Card className={`overflow-hidden ${STATUS_GROUPS.completed.includes(getOrderStatus(order)) ? 'opacity-75' : ''}`}>
        <CardHeader className={`pb-2 ${isDelivery ? 'bg-green-50' : 'bg-amber-50'}`}>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                {isDelivery ? (
                  <Truck className="h-4 w-4 text-green-600" />
                ) : (
                  <Bike className="h-4 w-4 text-amber-600" />
                )}
                <span>#{order.vendaID}</span>
              </CardTitle>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{getTimeElapsed(order.dataCriacao)}</span>
              </div>
            </div>
            <Badge className={getStatusColor(getOrderStatus(order))}>
              {statusLabel}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-3 space-y-3">
          {order.cliente?.nome && (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{order.cliente.nome}</span>
              {order.cliente.telefone && (
                <a href={`tel:${order.cliente.telefone}`} className="ml-auto text-primary hover:underline flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {order.cliente.telefone}
                </a>
              )}
            </div>
          )}

          {isDelivery && renderAddress(order)}

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" />
              {order.itens.length} {order.itens.length === 1 ? 'item' : 'itens'}
            </p>
            <ul className="text-sm space-y-0.5 max-h-24 overflow-y-auto">
              {order.itens.map(item => (
                <li key={`${order.vendaID}-${item.produtoID}`} className="flex justify-between">
                  <span>{item.quantidade}x {item.nome}</span>
                  <span className="text-muted-foreground">{formatCurrency(item.valor)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <div>
              {order.entregaStatus?.taxaEntrega && order.entregaStatus.taxaEntrega > 0 && (
                <p className="text-xs text-muted-foreground">
                  Taxa: {formatCurrency(order.entregaStatus.taxaEntrega)}
                </p>
              )}
              {order.entregaStatus?.motorista && (
                <p className="text-xs text-muted-foreground">
                  Motorista: {order.entregaStatus.motorista}
                </p>
              )}
            </div>
            <span className="font-bold text-primary">
              {formatCurrency(order.total + (order.entregaStatus?.taxaEntrega || 0))}
            </span>
          </div>

          {order.entregaStatus?.observacao && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 p-2 rounded flex items-start gap-2">
              <AlertCircle className="h-3 w-3 text-yellow-600 mt-0.5" />
              <span>{order.entregaStatus.observacao}</span>
            </div>
          )}

          {actions.length > 0 && (
            <div className="flex gap-2 pt-2">
              {actions.map(action => (
                <Button
                  key={action.status}
                  variant="default"
                  size="sm"
                  className="flex-1"
                  disabled={isUpdating}
                  onClick={() => updateDeliveryStatus(order, action.status)}
                >
                  {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : action.icon}
                  <span className="ml-1">{action.label}</span>
                </Button>
              ))}
              <Button variant="outline" size="sm" disabled>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Entregas & Retiradas</h1>
          <p className="text-muted-foreground text-sm">
            Consultado diretamente do ERP. Atualize os status em tempo real.
          </p>
        </div>
        <Button variant="outline" onClick={fetchOrders} disabled={loading} className="flex items-center gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
          Atualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 h-auto">
          {['all', 'pending', 'preparing', 'ready', 'out', 'completed'].map(tab => (
            <TabsTrigger key={tab} value={tab} className="flex-col py-2 px-1 text-xs">
              <span className="text-lg font-bold">
                {statusCounts[tab as keyof typeof statusCounts] ?? 0}
              </span>
              <span>
                {tab === 'all' && 'Todos'}
                {tab === 'pending' && 'Novos'}
                {tab === 'preparing' && 'Separação'}
                {tab === 'ready' && 'Pronto'}
                {tab === 'out' && 'Em Rota'}
                {tab === 'completed' && 'Finalizados'}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-8 w-8 mx-auto text-muted-foreground animate-spin mb-3" />
                <p className="text-muted-foreground">Carregando pedidos...</p>
              </CardContent>
            </Card>
          ) : filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Truck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Nenhum pedido nesta categoria</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOrders.map(order => (
                <OrderCard key={order.vendaID} order={order} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeliveryPage;
